import type { OutcomeMessage } from './shopifyEdit';

/**
 * "Deactivate all codes" for a campaign, in Shopify's words. Every code has its
 * own discount, so each deactivated code can be reactivated. Results are the
 * backend's per-code counts.
 */
export const shopifyDeactivateAllCopy = {
  button: 'Deactivate all codes',
  hiddenOnly: (n: number) =>
    `No codes for the creators you can see. ${n} code${n === 1 ? ' is' : 's are'} still active for creators not shown here.`,
  reasons: {
    nothingOn: 'Every code on this campaign is already deactivated.',
  },
  dialog: {
    title: 'Deactivate all codes on this campaign?',
    description:
      'Every Discovr code on this campaign stops working at checkout right away: creator codes, campaign codes and replaced codes.',
    warning:
      'Each code can be reactivated under Inactive codes; a code managed in Shopify is reactivated there. Generate skips creators whose code is deactivated. Sales history is kept.',
    countLabel: 'Codes to deactivate',
    activeLabel: 'Active codes',
    replacedLabel: 'Replaced codes still on',
    note: 'Discovr won’t message the creators. Tell them first if they’re already sharing.',
    confirm: 'Deactivate all codes',
    finality: 'You can reactivate each code later.',
    includesHidden: (n: number) =>
      `Includes ${n} code${n === 1 ? '' : 's'} of creators not shown here.`,
  },
  result: {
    alreadyOff: 'Every code was already deactivated.',
    failedGeneric: 'Couldn’t deactivate the codes',
    partialUnknown: 'Some codes may not have been deactivated. Check the list and try again.',
  },
} as const;

export interface DeactivateAllCounts {
  status?: 'invalidated' | 'partially_invalidated' | 'not_invalidated';
  turned_off: number;
  already_off: number;
  skipped_editing: number;
  failed: number;
}

const codes = (n: number) => `${n} code${n === 1 ? '' : 's'}`;

function outcomeSentences({ failed, skipped_editing: editing }: DeactivateAllCounts): string {
  const parts: string[] = [];
  if (failed > 0) parts.push(`${failed} couldn’t be deactivated and may still work at checkout.`);
  if (editing > 0) {
    parts.push(
      editing === 1
        ? '1 is being edited and stays as the edit leaves it.'
        : `${editing} are being edited and stay as the edit leaves them.`,
    );
  }
  parts.push(editing > 0 ? 'Try again in a couple of minutes.' : 'Try again.');
  return parts.join(' ');
}

/** The toast for Deactivate all codes, from the counts the server returned. */
export function deactivateAllMessage(res: DeactivateAllCounts): OutcomeMessage {
  const { turned_off: off, skipped_editing: editing, failed } = res;
  const leftOn = editing + failed;
  if (leftOn === 0 && res.status && res.status !== 'invalidated') {
    return { kind: 'warning', message: shopifyDeactivateAllCopy.result.partialUnknown };
  }
  if (leftOn === 0) {
    if (off === 0) return { kind: 'success', message: shopifyDeactivateAllCopy.result.alreadyOff };
    return { kind: 'success', message: `Deactivated ${codes(off)}.` };
  }
  const detail = outcomeSentences(res);
  if (off === 0) return { kind: 'error', message: `No codes were deactivated. ${detail}` };
  return { kind: 'warning', message: `Deactivated ${off} of ${codes(off + leftOn)}. ${detail}` };
}
